import { FC, useEffect, useState } from 'react';
import { uiInput } from 'components/BionicUI/CutCorners';
import { cn } from 'lib/utils';
import ProgressDisplay from './ProgressDisplay';
import { ROUTES } from 'routes';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { usePrevious } from '@react-hooks-library/core';
import { isEqual } from 'lodash';
import { UserRoles } from 'core/models/users/user.model';

export type Stage = 1 | 2 | 3;
export type StageStatus =
  | 'submitted'
  | 'pending'
  | 'in-progress'
  | 'complete'
  | 'rejected';
export type ProgressStage = {
  route?: keyof typeof ROUTES;
  id: Stage;
  status: StageStatus;
  buttonText?: string;
  remarks?: string;
};
interface ApplicationProgressBoxProps {
  progress?: ProgressStage[];
  lastUpdated?: DateTime;
  userRoles: UserRoles | undefined;
}

const ApplicationProgressBox: FC<ApplicationProgressBoxProps> = ({
  progress,
  lastUpdated,
  userRoles,
}) => {
  const navigate = useNavigate();
  const previousProgress = usePrevious(progress);
  const [keyUpdate, setKeyUpdate] = useState<string>(DateTime.now().toISO());

  useEffect(() => {
    if (!lastUpdated || !previousProgress || !progress) return;
    if (!isEqual(progress, previousProgress))
      setKeyUpdate(lastUpdated?.toISO() ?? '');
  }, [lastUpdated, progress, previousProgress]);

  function handleStageClick(stage: ProgressStage): void {
    if (stage.route) navigate(stage.route);
  }

  return (
    <div className="block relative justify-center page-side-padding animate-fade-in">
      <div className="block relative w-full">
        <Background />
        <div
          className={`grid relative grid-flow-row sm:grid-flow-col ${userRoles && userRoles.includes('PROJECT_OWNER') ? 'sm:grid-cols-3 grid-cols-1' : 'sm:grid-cols-2 grid-cols-1'} grid-cols-[auto_auto] sm:grid-rows-[auto_auto] gap-x-2 sm:gap-x-4 gap-y-3 sm:gap-y-1 w-full h-full min-h-[224px] p-10 pb-5 z-10`}
        >
          {progress?.map((stage, i) => (
            <ProgressDisplay
              key={`progress-box-${i}-${keyUpdate}`}
              stage={stage}
              onClick={() => handleStageClick(stage)}
              userRoles={userRoles}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const Background = () => {
  const base =
    'block absolute top-0 left-0 w-full h-full pointer-events-none z-0';

  return (
    <>
      <div className={cn(base, 'bg-white/10 backdrop-blur-xl', uiInput.bg)} />
      <div
        className={cn(
          base,
          'bg-gradient-to-b from-white/30 to-white/10',
          uiInput.border
        )}
      />
    </>
  );
};

export default ApplicationProgressBox;
